/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {Component, Inject, Input, NgZone} from '@angular/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import template from './creative-set-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdnResourceListService} from '../common/controller/adn-resource-list.service';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {advertiserFromNavigation, advertiserNavigationParams, folioFromNavigation} from '../../components/util/navigation-context';
import {getMatchingFee, simplifyFee} from '../line-item/line-item-fee-utils';
import {postFillGenerateFormatFees} from '../creative/creative-commons';
import {formatPercentage} from '../common/filters/format-percentage';
import {formatAdnStat} from '../common/filters/format-adn-stat';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {CreativeSetInfoResource} from '../../components/api/resources/resource/creative-set.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {SearchResource} from '../../components/api/resources/resource/search';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';

@Component({
  standalone: false,
  selector: 'adn-creative-set-edit-page',
  template
})
export class CreativeSetEditPageComponent extends AdminResourceEditPageBase {
  @Input() folioFromParam: any;
  @Input() advertiserFromParam: any;

  currencies: string[] = [];
  folioResults: any[] = [];
  uiFees: {fees: any[]} = {fees: []};
  teamsWarning = false;

  creativeListHost: any = {
    isCreativeSet: true,
    isNew: true,
    model: {},
    items: [],
    query: {$resolved: true},
    bulkSelection: {},
    bulkSelectSize: 0
  };

  private formatFeesCache: any[] = [];
  private numCreativeCache = -1;
  private creativeListInitialized = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(SearchResource) private readonly searchResource: any,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(CreativeSetInfoResource) private readonly CreativeSetInfo: any,
    private readonly adnListHelper: AdnListHelperService,
    private readonly $state: StateService,
    ngZone: NgZone,
    private readonly listService: AdnResourceListService,
    private readonly uiRouter: UIRouter,
    private readonly appLocale: AppLocaleService,
    private readonly navigationHistory: NavigationHistoryService
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    // CreativeSetInfo hydrates folio/advertiser after first paint; suppress dirty until then.
    this.deferFormDirtyTracking();
    this.currencies = LocalNetworkProfile.get('availableCurrencies') || [];
    this.creativeListHost.perms = LocalUserPermissions.getAllVisPermissions();
  }

  ngOnChanges(): void {
    super.ngOnChanges();
    this.syncCreativeListHost();
  }

  folioSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'Folio'}).then((data: any) => {
      this.ngZone.run(() => {
        this.folioResults = data.searchResults;
      });
    });
  };

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  onFolioChange(): void {
    this.markExternalFormDirty();
  }

  formatFee(fee: any): string {
    if (fee.type === 'PERCENTAGE') {
      return formatPercentage(fee.percentage / 100, 2, undefined, this.appLocale.getLocaleId());
    }
    return formatAdnStat(fee.monetary, undefined, undefined, undefined, this.LocalNetworkProfile.get('defaultCurrency'), this.appLocale.getLocaleId());
  }

  newBulkCreative(): void {
    this.$state.go('app.adv.creatives.bulkupload', _.assign({}, advertiserNavigationParams(this.editingModel.advertiser), {
      creativeSetId: this.editingModel.id,
      creativeSetName: this.editingModel.name
    }));
  }

  protected afterInitEditingModel(): void {
    this.editingModel.renderOption = this.editingModel.renderOption || null;

    if (this.isNewCreativeSet()) {
      const folio = this.folioFromParam || folioFromNavigation(this.navigationHistory, this.uiRouter.globals.params);
      if (folio?.id) {
        const folioCopy = _.cloneDeep(folio);
        this.editingModel.folio = folioCopy;
        this.model.folio = folioCopy;
        this.folioResults = [folioCopy];
      }
      const advertiser = this.advertiserFromParam || advertiserFromNavigation(this.navigationHistory, this.uiRouter.globals.params);
      if (advertiser?.id) {
        const advertiserCopy = _.cloneDeep(advertiser);
        this.editingModel.advertiser = advertiserCopy;
        this.model.advertiser = advertiserCopy;
      }
    }

    if (this.isNew && !this.editingModel.startDate) {
      const networkTimezone = this.LocalNetworkProfile.get('timeZone') || 'utc';
      this.editingModel.startDate = moment().tz(networkTimezone).startOf('day').toDate();
    }

    if (this.editingModel.startDate) {
      this.editingModel.modStartDate = this.editingModel.startDate;
    }
    if (this.editingModel.endDate) {
      this.editingModel.modEndDate = this.editingModel.endDate;
    }

    this.editingModel.userState = this.editingModel.userState || 'APPROVED';
    this.syncCreativeListHost();
    this.refreshCreativeList();
    this.getUiFees();

    // New CS ids are client-generated; CreativeSetInfo only exists after save.
    if (!this.isNewCreativeSet() && this.model?.id) {
      this.deferFormDirtyTracking();
      this.CreativeSetInfo.get({id: this.model.id}, (cs: any) => {
        this.ngZone.run(() => {
          if (_.get(cs, ['folio', 'id'])) {
            this.editingModel.folio = cs.folio;
          }
          if (_.get(cs, ['advertiser', 'id'])) {
            this.editingModel.advertiser = cs.advertiser;
          }
          this.creativeListHost.model = this.editingModel;
          this.publishModelUpdate();
          this.resumeFormDirtyTracking();
        });
      }, () => {
        this.ngZone.run(() => this.resumeFormDirtyTracking());
      });
    } else {
      this.resumeFormDirtyTracking();
    }
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (saveObj) {
      this.teamsWarning = (_.get(this.editingModel, 'teams') || []).length !== (_.get(saveObj, 'teams') || []).length;
      this.editingModel.renderOption = this.editingModel.renderOption || null;
      _.set(this.editingModel, ['creativeTagTechFeeCpm'], _.get(saveObj, ['creativeTagTechFeeCpm']));
    }
    this.editingModel.userState = this.editingModel.userState || 'APPROVED';
    this.syncCreativeListHost();
    this.refreshCreativeList();
    this.getUiFees();
  }

  protected onBeforeSubmit(): void {
    if (this.isNew) {
      LocalNetworkInfo.obtain().increment('creativeSetCount');
    }
    this.editingModel.startDate = _.isDate(this.editingModel.modStartDate) ? this.editingModel.modStartDate : null;
    this.editingModel.endDate = _.isDate(this.editingModel.modEndDate) ? this.editingModel.modEndDate : null;
  }

  private isNewCreativeSet(): boolean {
    return !!this.isNew;
  }

  private syncCreativeListHost(): void {
    this.creativeListHost.isCreativeSet = true;
    this.creativeListHost.isNew = !!this.isNew;
    this.creativeListHost.model = this.editingModel;
    this.creativeListHost.perms = this.LocalUserPermissions.getAllVisPermissions();
    this.creativeListHost.newBulkCreative = () => this.newBulkCreative();
    this.creativeListHost.updateRelData = this.creativeListHost.updateRelData || (() => undefined);
  }

  private refreshCreativeList(): void {
    if (this.isNew || !this.model?.id) {
      this.creativeListInitialized = false;
      return;
    }
    if (this.creativeListInitialized && this.creativeListHost.makeQuery) {
      this.creativeListHost.makeQuery();
      return;
    }
    this.creativeListInitialized = true;
    this.adnListHelper.setUpBasicList(this.creativeListHost, this.Creative, 'fullCreativesForCreativeSet', {
      moreParams: {creativeSet: this.model.id},
      concise: true,
      pageSize: 101,
      afterFill: () => {
        this.ngZone.run(() => {
          if (_.isFunction(this.creativeListHost.updateRelData)) {
            this.creativeListHost.updateRelData(this.creativeListHost.items, 'creatives');
          }
          const data = postFillGenerateFormatFees(this.creativeListHost);
          const refCurrency = this.getUiFees(data.creativeFormatFees, data.validItems.length);
          _.forEach(this.creativeListHost.items, (item: any) => {
            const fee = simplifyFee(this.LocalNetworkProfile, refCurrency, item.creativeFormatFee);
            if (fee && fee.monetary.amount > 0) {
              item.simplifiedCreativeFormatFee = fee;
            }
          });
        });
      }
    });
    this.listService.ensureAngularList(this.creativeListHost);
  }

  private getUiFees(creativeFormatFees?: any[], numCreatives?: number): any {
    const referenceCurrency = _.cloneDeep(_.get(this, ['editingModel', 'billingCurrency']));
    this.uiFees = this.uiFees || {fees: []};
    this.uiFees.fees.length = 0;

    if (_.get(this.editingModel, ['creativeTagTechFeeCpm', 'type'])) {
      const cpmTechFee = _.cloneDeep(this.editingModel.creativeTagTechFeeCpm);
      cpmTechFee.name = cpmTechFee.name || this.LocalNetworkProfile.getNetworkTranslations('creativeTagFee');
      cpmTechFee.id = cpmTechFee.id || 'creative-tag-tech-fee';
      this.uiFees.fees.push(cpmTechFee);
    }
    if (creativeFormatFees) {
      this.numCreativeCache = numCreatives;
      this.formatFeesCache.length = 0;
      _.forEach(creativeFormatFees, (f) => {
        const fee = simplifyFee(this.LocalNetworkProfile, referenceCurrency, f);
        if (fee) {
          this.formatFeesCache.push(fee);
        }
      });
    }
    if (this.formatFeesCache.length) {
      const uniqFees = _.uniqBy(this.formatFeesCache, (ff: any) => {
        if (ff.monetary.currency === referenceCurrency) {
          return ff.monetary.amount;
        }
      });
      if (uniqFees.length) {
        const ff: any = {
          name: this.LocalNetworkProfile.getNetworkTranslations('creativeFormatFee'),
          id: 'creative-format-fee',
          type: 'MONETARY',
          monetary: {
            currency: uniqFees[0].monetary.currency
          }
        };
        if (uniqFees.length === 1 && this.numCreativeCache === this.formatFeesCache.length) {
          ff.monetary.amount = uniqFees[0].monetary.amount;
        } else {
          const minFee = this.numCreativeCache > this.formatFeesCache.length
            ? {monetary: {amount: 0}}
            : _.sortBy(uniqFees, ['monetary.amount'])[0];
          const maxFee = _.sortBy(uniqFees, ['monetary.amount'])[uniqFees.length - 1];
          ff.monetary.amount = minFee.monetary.amount;
          ff.monetary.maxAmount = maxFee.monetary.amount;
        }
        this.uiFees.fees.push(ff);
      }
    }
    const standardiseFees = (fees: any[]) => {
      _.forEach(fees, (theFee) => {
        if (theFee.type === 'MULTI_CURRENCY_MONETARY') {
          const matchingFee = getMatchingFee(theFee.multiCurrencyMonetaryAmounts, referenceCurrency);
          if (matchingFee) {
            theFee.monetary = matchingFee;
          }
        }
      });
    };
    standardiseFees(this.uiFees.fees);
    if (creativeFormatFees) {
      standardiseFees(creativeFormatFees);
    }
    return referenceCurrency;
  }
}
